import type {
	PrefetchContext,
	PrefetchedDataProps,
	PrefetchedUserDataProps,
	PrefetchUserContext,
} from '#src/shared/prefetch';

import { DECISION_MAKER_HASH_QUERY_PARAM_NAME } from '#src/app/pages/petitionDetails/shared/constants';
import { useCountryCode, useLocale } from '#src/app/shared/hooks/l10n';
import { useQueryString } from '#src/app/shared/hooks/location';
import { useIsGuest, useSessionAsync } from '#src/app/shared/hooks/session';
import type { MergedState } from '#src/app/shared/hooks/state';
import { useMergedStates } from '#src/app/shared/hooks/state';
import { useIsBot } from '#src/app/shared/hooks/userAgent';
import { isLoaded } from '#src/app/shared/utils/async';
import { getQueryStringSingleValue } from '#src/app/shared/utils/query';

import { prefetchPetitionDetailsPageDataFromApi, usePetitionDetailsPageDataFromApi } from './pageData';
import { prefetchPetitionDetailsPageUserDataFromApi, usePetitionDetailsPageUserDataFromApi } from './pageUserData';

export type PetitionDetailsPagePrefetchedContext = Readonly<{
	pageData: NonNullable<Parameters<typeof usePetitionDetailsPageDataFromApi>[0]>;
}>;

export type PetitionDetailsPagePrefetchedUserContext = NonNullable<
	Parameters<typeof usePetitionDetailsPageUserDataFromApi>[0]
>;

export type PetitionDetailsPageCombinedState = MergedState<
	ReturnType<typeof usePetitionDetailsPageDataFromApi>,
	ReturnType<typeof usePetitionDetailsPageUserDataFromApi>
>;

export function usePageContext(
	slug: string,
	prefetchedData?: PetitionDetailsPagePrefetchedContext,
	prefetchedUserData?: PetitionDetailsPagePrefetchedUserContext,
): PetitionDetailsPageCombinedState {
	const sessionState = useSessionAsync();
	const session = isLoaded(sessionState) ? sessionState.value : undefined;
	const query = useQueryString();
	const isGuest = useIsGuest();
	const isBot = useIsBot();

	const hasShowLegislativeBodiesParam = getQueryStringSingleValue(query, 'showLBs') === 'true';

	return useMergedStates(
		usePetitionDetailsPageDataFromApi(prefetchedData?.pageData, slug, {
			countryCode: useCountryCode(),
			locale: useLocale(),
			hasShowLegislativeBodiesParam,
			isBot,
		}),
		usePetitionDetailsPageUserDataFromApi(prefetchedUserData, slug, {
			currentUserId: session?.user?.id,
			loginState: isGuest ? 'GUEST' : session?.loginState,
			decisionMakerHash: getQueryStringSingleValue(query, DECISION_MAKER_HASH_QUERY_PARAM_NAME),
			hasShowLegislativeBodiesParam,
		}),
	);
}

export async function prefetchPageContext(
	slug: string,
	context: PrefetchContext,
): Promise<PetitionDetailsPagePrefetchedContext> {
	const pageData = await prefetchPetitionDetailsPageDataFromApi(context, slug, {
		...context.l10n,
		hasShowLegislativeBodiesParam: getQueryStringSingleValue(context.query, 'showLBs') === 'true',
		isBot: context.bot,
	});

	return { pageData };
}

export async function prefetchPageUserContext(
	slug: string,
	context: PrefetchUserContext,
): Promise<PetitionDetailsPagePrefetchedUserContext> {
	const pageUserData = await prefetchPetitionDetailsPageUserDataFromApi(context, slug, {
		currentUserId: context.session.user?.id,
		loginState: context.session.loginState,
		decisionMakerHash: getQueryStringSingleValue(context.query, DECISION_MAKER_HASH_QUERY_PARAM_NAME),
		hasShowLegislativeBodiesParam: getQueryStringSingleValue(context.query, 'showLBs') === 'true',
	});

	return pageUserData;
}

export type PetitionDetailsPagePrefetchedDataProps = PrefetchedDataProps<PetitionDetailsPagePrefetchedContext> &
	PrefetchedUserDataProps<PetitionDetailsPagePrefetchedUserContext>;
